import MapboxDraw from '@mapbox/mapbox-gl-draw';
import { Map, NavigationControl } from 'mapbox-gl';
import React, { useCallback, useEffect, useRef } from 'react';
import '@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css'; /* Style used for the Mapbox Draw */
import { useKeyPress } from 'utils/useKeyPress';
import { FeatureCollection } from 'geojson';

interface DrawPolygonProps {
  map: Map;
  shouldClearPolygon: boolean;
  onCreate: (polygonData: FeatureCollection) => void;
  onUpdate: (polygonData: FeatureCollection) => void;
  onReset: () => void;
}

export const DrawPolygon = ({
  map,
  onCreate,
  onUpdate,
  onReset,
  ...props
}: DrawPolygonProps) => {
  const draw = useRef<MapboxDraw>(
    new MapboxDraw({
      displayControlsDefault: false,
      /* Select which mapbox-gl-draw control buttons to add to the map */
      controls: {
        polygon: true,
        trash: true,
      },
    })
  );

  /* Shortcut to use Polygon by pressin S */
  useKeyPress('s', {
    keydownCallback: () => {
      draw.current.changeMode('draw_polygon');
    },
  });

  /* Shortcut to use Polygon by pressin P */
  useKeyPress('p', {
    keydownCallback: () => {
      draw.current.changeMode('draw_polygon');
    },
  });

  useEffect(() => {
    /* Make drawing polygon feature visible on the Bottom-right position of the Map */
    map.addControl(new NavigationControl(), 'bottom-right');
    map.addControl(draw.current, 'bottom-right');
  }, [map]);

  const resetPolygon = useCallback(() => {
    draw.current.deleteAll();
    onReset();
  }, [onReset]);

  useEffect(() => {
    /* Ability to control when to reset the Polygon */
    if (props.shouldClearPolygon) {
      resetPolygon();
    }
  }, [props.shouldClearPolygon, resetPolygon]);

  useEffect(() => {
    /* Only 1 polygon should exist to prevent error */
    /* Existing behavior :
     * only get orders inside the "first" polygon area
     */
    const onDrawUpdate = () => {
      /* Always remove the previous polygon drew */
      const drawings = draw.current.getAll().features as any[];
      if (drawings.length > 1) {
        draw.current.delete(drawings[0].id);
      }

      /* Send update */
      const data = draw.current.getAll();
      onUpdate(data);
    };

    const onDrawDelete = () => {
      resetPolygon();
    };

    map.on('draw.create', onDrawUpdate);
    map.on('draw.update', onDrawUpdate);
    map.on('draw.delete', onDrawDelete);

    return () => {
      map.off('draw.create', onDrawUpdate);
      map.off('draw.update', onDrawUpdate);
      map.off('draw.delete', onDrawDelete);
    };
  }, [map, onCreate, onUpdate, resetPolygon]);

  return <></>;
};
